import * as logClient from "@classdojo/log-client";
import { Env } from "@classdojo/log-client/lib/client/types";
import createAction from "@web-monorepo/infra/createAction";
import createActionCreator from "@web-monorepo/infra/createActionCreator";
import { SagaIterator } from "redux-saga";
import { call, take, select } from "redux-saga/effects";
import { FETCH_SESSION_DONE, userSelector, selectEntryPointUrl } from "pods/auth";
import { setUser } from "@web-monorepo/telemetry";

type Event = {
  eventName: string;
  metadata?: Record<string, unknown>;
};

logClient.init({
  env: getLogClientEnv(),
  site: "toolkit",
});

export const LOG_EVENT = createAction("logEvent");

export const logEvent = createActionCreator(LOG_EVENT, (event: Event) => event);

function getLogClientEnv(): Env {
  return (
    {
      production: "prod",
      development: "dev",
      test: "testing",
    } as const
  )[Config.nodeEnv];
}

export function* logEventSaga(): SagaIterator<void> {
  let sessionLoaded = false;
  const logQueue = [];

  while (true) {
    const action: {
      type: string;
      error: Error;
      payload: Event;
    } = yield take([FETCH_SESSION_DONE, LOG_EVENT]);

    if (action.type === FETCH_SESSION_DONE) {
      sessionLoaded = true;

      if (!action.error) {
        const user: { emailAddress: string; _id: string } = yield select(userSelector);
        setUser({ id: user._id });
        yield call(logClient.setEntityId, user._id);
      }

      while (logQueue.length) {
        const logAction = logQueue.shift();
        yield* log(logAction?.payload || "");
      }

      continue;
    }

    if (!sessionLoaded) {
      logQueue.push(action);
      continue;
    }

    yield* log(action.payload);
  }
}

function* log(event: string | Event) {
  const entryPoint: string = yield select(selectEntryPointUrl);

  if (typeof event === "string") {
    event = { eventName: event };
  }

  if (typeof event !== "string") {
    if (!event.metadata) {
      event.metadata = {};
    }
    event.metadata.experiment = entryPoint;
  }

  yield call(logClient.logEvent, event);
}
